import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Chip, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Alert, CircularProgress, Avatar, Tooltip, LinearProgress,
  Collapse, IconButton,
} from '@mui/material';
import { ExpandMore, ExpandLess, Balance, Warning, TrendingDown, TrendingUp, TrendingFlat } from '@mui/icons-material';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const fmt = (n: number, currency = 'HKD') =>
  new Intl.NumberFormat('en-HK', { style: 'currency', currency, maximumFractionDigits: 0 }).format(n);

const GENDER_CFG: Record<string, { label: string; color: string; emoji: string }> = {
  male:            { label: 'Male',             color: '#1565c0', emoji: '👨' },
  female:          { label: 'Female',           color: '#880e4f', emoji: '👩' },
  non_binary:      { label: 'Non-binary',       color: '#4527a0', emoji: '🧑' },
  prefer_not_to_say:{ label: 'Prefer not to say', color: '#546e7a', emoji: '—' },
  unknown:         { label: 'Not recorded',     color: '#90a4ae', emoji: '?' },
};

function GapBadge({ gap }: { gap: number | null }) {
  if (gap === null) return <Chip size="small" label="N/A" sx={{ fontSize: '0.65rem' }} />;
  const color = Math.abs(gap) < 5 ? '#2e7d32' : Math.abs(gap) < 15 ? '#f57f17' : '#b71c1c';
  const icon = gap > 1 ? <TrendingUp fontSize="small" /> : gap < -1 ? <TrendingDown fontSize="small" /> : <TrendingFlat fontSize="small" />;
  return (
    <Chip size="small" icon={icon}
      label={`${gap > 0 ? '+' : ''}${gap}%`}
      sx={{ bgcolor: color + '15', color, fontWeight: 700, fontSize: '0.7rem' }} />
  );
}

function SpreadBar({ min, max, mean, p25, p75, currency }: any) {
  const range = max - min || 1;
  const pct = (v: number) => Math.round(((v - min) / range) * 100);
  return (
    <Tooltip title={`P25: ${fmt(p25, currency)}  Mean: ${fmt(mean, currency)}  P75: ${fmt(p75, currency)}`}>
      <Box sx={{ position: 'relative', height: 8, bgcolor: '#e0e0e0', borderRadius: 4, minWidth: 120, my: 0.5 }}>
        <Box sx={{
          position: 'absolute', left: `${pct(p25)}%`, width: `${pct(p75) - pct(p25)}%`,
          height: '100%', bgcolor: '#1565c020', borderRadius: 4,
        }} />
        <Box sx={{
          position: 'absolute', left: `${pct(mean)}%`, top: -2,
          width: 3, height: 12, bgcolor: '#1565c0', borderRadius: 1,
        }} />
      </Box>
    </Tooltip>
  );
}

interface Summary {
  totalOnFile: number; noGenderData: number; globalMean: number; globalMedian: number;
  p25: number; p75: number; meanGpg: number | null; medianGpg: number | null;
  flaggedRoles: number; maleCount: number; femaleCount: number; currency: string;
}
interface GenderRow { gender: string; count: number; meanSalary: number; medianSalary: number; p25: number; p75: number; meanGapVsMale: number | null; medianGapVsMale: number | null }
interface DeptRow { department: string; headcount: number; mean: number; median: number; min: number; max: number; p25: number; p75: number; spreadPct?: number; vsGlobalMedian: number; genderSplit: Record<string, number> }
interface PosRow { position: string; headcount: number; mean: number; median: number; min: number; max: number; spreadPct: number; flagged: boolean; employees: any[] }
interface TenureRow { band: string; count: number; mean: number; median: number }

interface Dashboard { summary: Summary; genderGap: GenderRow[]; byDepartment: DeptRow[]; byPosition: PosRow[]; byEmploymentType: any[]; tenurePay: TenureRow[] }

export default function PayEquity() {
  const [tab, setTab] = useState(0);
  const [data, setData] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedPos, setExpandedPos] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    apiFetch('/api/pay-equity/dashboard')
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  const s = data?.summary;
  const cur = s?.currency || 'HKD';
  const TABS = ['Overview', 'Gender Gap', 'By Department', 'By Role', 'Tenure & Pay'];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Avatar sx={{ bgcolor: '#1a237e', width: 48, height: 48 }}><Balance /></Avatar>
        <Box>
          <Typography variant="h5" fontWeight={700}>Pay Equity Analysis</Typography>
          <Typography variant="body2" color="text.secondary">
            Gender gap · department equity · within-role spread · tenure correlation
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* KPI row */}
      {s && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Employees on File',   value: s.totalOnFile,                          color: '#1565c0' },
            { label: 'Global Median Salary', value: fmt(s.globalMedian, cur),               color: '#2e7d32' },
            { label: 'P25 – P75 Range',      value: `${fmt(s.p25, cur)} – ${fmt(s.p75, cur)}`, color: '#4527a0', small: true },
            { label: 'Mean Gender Pay Gap',  value: s.meanGpg !== null ? `${s.meanGpg > 0 ? '+' : ''}${s.meanGpg}%` : 'N/A', color: s.meanGpg !== null && Math.abs(s.meanGpg) > 10 ? '#b71c1c' : '#2e7d32' },
            { label: 'Flagged Roles (>30% spread)', value: s.flaggedRoles,                 color: s.flaggedRoles > 0 ? '#e65100' : '#2e7d32' },
            { label: 'No Gender Data',       value: `${s.noGenderData} emp`,               color: '#78909c' },
          ].map(k => (
            <Grid size={{ xs: 6, sm: 4, md: 2 }} key={k.label}>
              <Paper sx={{ p: 1.5, textAlign: 'center', borderTop: `3px solid ${k.color}`, height: '100%' }}>
                <Typography variant={k.small ? 'body2' : 'h5'} fontWeight={700} color={k.color}>{k.value}</Typography>
                <Typography variant="caption" color="text.secondary">{k.label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {s && s.noGenderData > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>{s.noGenderData} employee{s.noGenderData !== 1 ? 's' : ''}</strong> have no gender recorded — gender gap figures may be incomplete.
          Update employee profiles to improve data coverage.
        </Alert>
      )}
      {s && s.flaggedRoles > 0 && (
        <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
          <strong>{s.flaggedRoles} role{s.flaggedRoles !== 1 ? 's' : ''}</strong> have a salary spread &gt;30% of the mean within the same position. Review the <em>By Role</em> tab.
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        {TABS.map((t, i) => <Tab key={i} label={t} />)}
      </Tabs>

      {/* ── Overview ── */}
      {tab === 0 && s && (
        <Grid container spacing={3}>
          {/* Gender split pie-like summary */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Gender Representation</Typography>
              {data?.genderGap.map(g => {
                const cfg = GENDER_CFG[g.gender] ?? GENDER_CFG.unknown;
                const pct = s.totalOnFile ? Math.round((g.count / s.totalOnFile) * 100) : 0;
                return (
                  <Box key={g.gender} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">{cfg.emoji} {cfg.label}</Typography>
                      <Typography variant="body2" fontWeight={600}>{g.count} ({pct}%)</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={pct}
                      sx={{ height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { bgcolor: cfg.color } }} />
                    <Typography variant="caption" color="text.secondary">
                      Median: {fmt(g.medianSalary, cur)} · Mean: {fmt(g.meanSalary, cur)}
                    </Typography>
                  </Box>
                );
              })}
            </Paper>
          </Grid>

          {/* Pay distribution */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Salary Distribution by Department</Typography>
              {data?.byDepartment.slice(0, 8).map(d => (
                <Box key={d.department} sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                    <Typography variant="body2">{d.department}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">{d.headcount} ppl</Typography>
                      <GapBadge gap={d.vsGlobalMedian} />
                    </Box>
                  </Box>
                  <SpreadBar min={d.min} max={d.max} mean={d.mean} p25={d.p25} p75={d.p75} currency={cur} />
                  <Typography variant="caption" color="text.secondary">
                    {fmt(d.min, cur)} – {fmt(d.max, cur)} · Median {fmt(d.median, cur)}
                  </Typography>
                </Box>
              ))}
            </Paper>
          </Grid>

          {/* Tenure vs pay */}
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Salary by Tenure Band</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Tenure</TableCell>
                      <TableCell align="right">Headcount</TableCell>
                      <TableCell align="right">Mean Salary</TableCell>
                      <TableCell align="right">Median Salary</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data?.tenurePay.map(t => (
                      <TableRow key={t.band} hover>
                        <TableCell><Typography variant="body2" fontWeight={600}>{t.band}</Typography></TableCell>
                        <TableCell align="right">{t.count}</TableCell>
                        <TableCell align="right">{fmt(t.mean, cur)}</TableCell>
                        <TableCell align="right">{fmt(t.median, cur)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* ── Gender Gap ── */}
      {tab === 1 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Gender</TableCell>
                <TableCell align="right">Count</TableCell>
                <TableCell align="right">Mean Salary</TableCell>
                <TableCell align="right">Median Salary</TableCell>
                <TableCell align="right">P25</TableCell>
                <TableCell align="right">P75</TableCell>
                <TableCell align="center">Mean Gap vs Male</TableCell>
                <TableCell align="center">Median Gap vs Male</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.genderGap.map(g => {
                const cfg = GENDER_CFG[g.gender] ?? GENDER_CFG.unknown;
                return (
                  <TableRow key={g.gender} hover>
                    <TableCell>
                      <Chip size="small" label={`${cfg.emoji} ${cfg.label}`}
                        sx={{ bgcolor: cfg.color + '15', color: cfg.color }} />
                    </TableCell>
                    <TableCell align="right">{g.count}</TableCell>
                    <TableCell align="right">{fmt(g.meanSalary, cur)}</TableCell>
                    <TableCell align="right">{fmt(g.medianSalary, cur)}</TableCell>
                    <TableCell align="right"><Typography variant="caption">{fmt(g.p25, cur)}</Typography></TableCell>
                    <TableCell align="right"><Typography variant="caption">{fmt(g.p75, cur)}</Typography></TableCell>
                    <TableCell align="center"><GapBadge gap={g.meanGapVsMale} /></TableCell>
                    <TableCell align="center"><GapBadge gap={g.medianGapVsMale} /></TableCell>
                  </TableRow>
                );
              })}
              {!data?.genderGap.length && (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No salary data found. Ensure salary records are on file.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── By Department ── */}
      {tab === 2 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Department</TableCell>
                <TableCell align="right">HC</TableCell>
                <TableCell align="right">Mean</TableCell>
                <TableCell align="right">Median</TableCell>
                <TableCell align="right">Min</TableCell>
                <TableCell align="right">Max</TableCell>
                <TableCell>Distribution</TableCell>
                <TableCell align="center">vs Global Median</TableCell>
                <TableCell>Gender Split</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.byDepartment.map(d => (
                <TableRow key={d.department} hover>
                  <TableCell><Typography variant="body2" fontWeight={600}>{d.department}</Typography></TableCell>
                  <TableCell align="right">{d.headcount}</TableCell>
                  <TableCell align="right">{fmt(d.mean, cur)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(d.median, cur)}</TableCell>
                  <TableCell align="right"><Typography variant="caption">{fmt(d.min, cur)}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption">{fmt(d.max, cur)}</Typography></TableCell>
                  <TableCell sx={{ minWidth: 100 }}>
                    <SpreadBar min={d.min} max={d.max} mean={d.mean} p25={d.p25} p75={d.p75} currency={cur} />
                  </TableCell>
                  <TableCell align="center"><GapBadge gap={d.vsGlobalMedian} /></TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {Object.entries(d.genderSplit).map(([g, n]) => (
                        <Chip key={g} size="small"
                          label={`${GENDER_CFG[g]?.emoji ?? '?'} ${n}`}
                          sx={{ fontSize: '0.6rem', height: 18, bgcolor: (GENDER_CFG[g]?.color ?? '#888') + '15', color: GENDER_CFG[g]?.color ?? '#888' }} />
                      ))}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── By Role ── */}
      {tab === 3 && (
        <>
          {data?.byPosition.filter(p => p.flagged).length === 0 ? null : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Rows highlighted in amber have a salary spread &gt;30% within the same role — investigate for potential pay equity issues.
            </Alert>
          )}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Position</TableCell>
                  <TableCell align="right">HC</TableCell>
                  <TableCell align="right">Min</TableCell>
                  <TableCell align="right">Mean</TableCell>
                  <TableCell align="right">Max</TableCell>
                  <TableCell align="center">Spread</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.byPosition.map(p => (
                  <React.Fragment key={p.position}>
                    <TableRow hover
                      sx={{ bgcolor: p.flagged ? '#fff8e140' : undefined, cursor: 'pointer' }}
                      onClick={() => setExpandedPos(expandedPos === p.position ? null : p.position)}>
                      <TableCell sx={{ width: 32, pr: 0 }}>
                        <IconButton size="small">
                          {expandedPos === p.position ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight={600}>{p.position}</Typography>
                          {p.flagged && <Chip size="small" label="⚠️ High Spread" sx={{ bgcolor: '#fff3e0', color: '#e65100', fontSize: '0.6rem' }} />}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{p.headcount}</TableCell>
                      <TableCell align="right">{fmt(p.min, cur)}</TableCell>
                      <TableCell align="right">{fmt(p.mean, cur)}</TableCell>
                      <TableCell align="right">{fmt(p.max, cur)}</TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={`${p.spreadPct}%`}
                          sx={{ bgcolor: p.flagged ? '#fff3e0' : '#e8f5e9', color: p.flagged ? '#e65100' : '#2e7d32', fontWeight: 700, fontSize: '0.65rem' }} />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                        <Collapse in={expandedPos === p.position} timeout="auto" unmountOnExit>
                          <Box sx={{ m: 1.5, ml: 6 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Employee</TableCell>
                                  <TableCell>Department</TableCell>
                                  <TableCell>Gender</TableCell>
                                  <TableCell align="right">Salary</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {p.employees.sort((a, b) => b.salary - a.salary).map((e, i) => (
                                  <TableRow key={i}>
                                    <TableCell>{e.name}</TableCell>
                                    <TableCell><Typography variant="caption">{e.dept}</Typography></TableCell>
                                    <TableCell>
                                      <Typography variant="caption">{GENDER_CFG[e.gender]?.emoji ?? '—'} {GENDER_CFG[e.gender]?.label ?? 'Not recorded'}</Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(e.salary, cur)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
                {!data?.byPosition.length && (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Not enough data — need at least 2 employees in the same role.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* ── Tenure & Pay ── */}
      {tab === 4 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>Salary vs Tenure Bands</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Shows whether longer-tenured employees earn meaningfully more. A flat curve may indicate lack of pay progression.
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Tenure Band</TableCell>
                  <TableCell align="right">Headcount</TableCell>
                  <TableCell align="right">Mean Salary</TableCell>
                  <TableCell align="right">Median Salary</TableCell>
                  <TableCell>Median Bar</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  const rows = data?.tenurePay ?? [];
                  const maxMed = Math.max(...rows.map(r => r.median), 1);
                  return rows.map(t => (
                    <TableRow key={t.band} hover>
                      <TableCell><Typography variant="body2" fontWeight={600}>{t.band}</Typography></TableCell>
                      <TableCell align="right">{t.count}</TableCell>
                      <TableCell align="right">{fmt(t.mean, cur)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(t.median, cur)}</TableCell>
                      <TableCell sx={{ minWidth: 160 }}>
                        <LinearProgress variant="determinate" value={Math.round((t.median / maxMed) * 100)}
                          sx={{ height: 10, borderRadius: 5,
                            '& .MuiLinearProgress-bar': { bgcolor: '#1565c0' } }} />
                      </TableCell>
                    </TableRow>
                  ));
                })()}
              </TableBody>
            </Table>
          </TableContainer>
          {data?.byEmploymentType.length ? (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>By Employment Type</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Count</TableCell>
                      <TableCell align="right">Mean</TableCell>
                      <TableCell align="right">Median</TableCell>
                      <TableCell align="right">P25</TableCell>
                      <TableCell align="right">P75</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.byEmploymentType.map((t: any) => (
                      <TableRow key={t.type} hover>
                        <TableCell><Chip size="small" label={t.type} /></TableCell>
                        <TableCell align="right">{t.count}</TableCell>
                        <TableCell align="right">{fmt(t.mean, cur)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(t.median, cur)}</TableCell>
                        <TableCell align="right"><Typography variant="caption">{fmt(t.p25, cur)}</Typography></TableCell>
                        <TableCell align="right"><Typography variant="caption">{fmt(t.p75, cur)}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : null}
        </Paper>
      )}
    </Box>
  );
}
